/**
 * The pieces every page is built from: the frame, links that route in place,
 * a party as the graph knows it, a grant list with provenance on every row,
 * and the loading and empty states.
 *
 * `PartyLink` is where the listing rule reaches the screen (invariant 12): a
 * listed organization is a link to its page, an unlisted EIN is a name with
 * its number and no link, a name alone is the name the filer wrote, and a
 * sponsored project is shown under its sponsor. `GrantRow` is where invariant
 * 8 reaches the screen: the source, the filing or page or award, and the
 * period stand beside every amount. Nothing here shows a street address or a
 * person; the items carry neither.
 * The header carries the search box (`search/SearchBox.tsx`), which opens any
 * organization by name, city, or EIN from every page.
 */
import type { ReactNode } from 'react';
import type { GrantEdge, Party, Provenance } from '../../src/serve/items.js';
import { formatAmount, formatCount, formatEin, formatPeriod, identifierTail, returnUrl, sourceLabel } from './format.js';
import { COPY, type ReadingSection } from './copy.js';
import { SearchBox } from './search/SearchBox.js';
import { displayName } from './names.js';
import { onLinkClick } from './router.js';

/** A link's look is either the site's (plain or quiet) or the caller's own class list, never both. */
type LinkLook = { readonly className: string; readonly quiet?: never } | { readonly className?: never; readonly quiet?: boolean };

export const Link = ({
  to,
  children,
  current = false,
  title,
  onNavigate,
  ...look
}: {
  to: string;
  children: ReactNode;
  current?: boolean;
  title?: string | undefined;
  /** Runs before an in-site navigation and not before a modified click, which the browser handles. */
  onNavigate?: () => void;
} & LinkLook) => (
  <a
    href={to}
    onClick={onLinkClick(to, onNavigate)}
    title={title}
    aria-current={current ? 'page' : undefined}
    className={look.className ?? (look.quiet === true ? 'text-teal-800 hover:underline decoration-teal-300 underline-offset-2' : 'text-teal-800 underline decoration-teal-300 underline-offset-2 hover:decoration-teal-700')}
  >
    {children}
  </a>
);

export const Frame = ({ children }: { children: ReactNode }) => (
  <div className="min-h-screen flex flex-col">
    <header className="border-b border-stone-200 bg-white">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
        <Link to="/" className="text-lg font-semibold tracking-tight text-stone-900 no-underline">
          FunderGraph
        </Link>
        <span className="hidden text-sm text-stone-500 sm:inline">{COPY.site.tagline}</span>
        {/* One link, not a menu: the documentation's own landing page lists every page, and the header must stay one row beside the search box. */}
        <nav aria-label="Site" className="text-sm">
          <Link to="/about" quiet>
            {COPY.site.about}
          </Link>
        </nav>
        <div className="w-full md:ml-auto md:w-auto">
          <SearchBox />
        </div>
      </div>
    </header>
    <main className="mx-auto w-full max-w-5xl px-4 py-6 flex-1">{children}</main>
    <footer className="border-t border-stone-200 text-xs text-stone-500">
      <div className="mx-auto max-w-5xl px-4 py-4">
        {COPY.site.footer}{' '}
        <Link to="/about" className="text-teal-800 hover:underline">
          About FunderGraph
        </Link>
        {' · '}
        <Link to="/about/reading" className="text-teal-800 hover:underline">
          How FunderGraph reads a filing
        </Link>
      </div>
    </footer>
  </div>
);

export const Section = ({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) => (
  <section className="mt-8">
    <div className="flex items-baseline justify-between gap-4 border-b border-stone-200 pb-1">
      <h2 className="text-base font-semibold text-stone-800">{title}</h2>
      {aside === undefined ? null : <div className="text-sm text-stone-500">{aside}</div>}
    </div>
    <div className="mt-3">{children}</div>
  </section>
);

/** One muted line under a section heading that says what the section's relationship is, with the way to the long form. */
export const Hint = ({ children, about }: { children: ReactNode; about?: ReadingSection }) => (
  <p className="mb-2 text-xs text-stone-500">
    {children}
    {about === undefined ? null : (
      <>
        {' '}
        <Link to={`/about/reading#${about}`} className="text-teal-800 hover:underline">
          Learn more
        </Link>
      </>
    )}
  </p>
);

export interface Tab {
  readonly label: string;
  readonly to: string;
  readonly current: boolean;
}

/** The views of one entity, as a row of links; the current one is marked for the reader and for assistive technology. */
export const Tabs = ({ label, items }: { label: string; items: readonly Tab[] }) => (
  <nav aria-label={label} className="mt-4 flex gap-4 border-b border-stone-200 text-sm">
    {items.map((tab) => (
      <Link
        key={tab.to}
        to={tab.to}
        current={tab.current}
        className={`-mb-px border-b-2 px-1 pb-2 ${tab.current ? 'border-teal-700 font-medium text-stone-900' : 'border-transparent text-stone-600 hover:border-stone-300 hover:text-stone-900'}`}
      >
        {tab.label}
      </Link>
    ))}
  </nav>
);

/** The shape of a page while it loads, with the words for a screen reader; a page that flashes "Loading…" as text jumps when the content lands. */
export const Loading = ({ what, compact = false }: { what: string; compact?: boolean }) => (
  <div role="status" aria-live="polite">
    <span className="sr-only">Loading {what}…</span>
    <div className="motion-safe:animate-pulse space-y-3" aria-hidden="true">
      {compact ? (
        <div className="h-4 w-1/2 rounded bg-stone-100" />
      ) : (
        <>
          <div className="h-7 w-2/3 rounded bg-stone-200" />
          <div className="h-4 w-1/2 rounded bg-stone-200" />
          <div className="mt-6 h-4 w-full rounded bg-stone-100" />
          <div className="h-4 w-5/6 rounded bg-stone-100" />
          <div className="h-4 w-4/6 rounded bg-stone-100" />
        </>
      )}
    </div>
  </div>
);

export const Empty = ({ children }: { children: ReactNode }) => <p className="text-stone-500">{children}</p>;

export const Problem = ({ error }: { error: Error }) => (
  <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-red-800">
    {error.message}
  </p>
);

export const Badge = ({ children, tone = 'stone' }: { children: ReactNode; tone?: 'stone' | 'teal' | 'amber' }) => {
  const tones = { stone: 'bg-stone-100 text-stone-700', teal: 'bg-teal-50 text-teal-800', amber: 'bg-amber-50 text-amber-900' } as const;
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs ${tones[tone]}`}>{children}</span>;
};

/** A share of a whole as a short bar beside the number, so a list of ten counts reads as a distribution at a glance. */
export const Bar = ({ share, label }: { share: number; label: string }) => (
  <span className="inline-block h-2 w-24 overflow-hidden rounded-sm bg-stone-100 align-middle" role="img" aria-label={label}>
    <span className="block h-2 bg-teal-600" style={{ width: `${Math.max(0, Math.min(100, Math.round(share * 100)))}%` }} />
  </span>
);

/** A native details element with the site's styling; the long paragraph a reader may not want is one click away, not gone. */
export const Disclosure = ({ summary, children, open = false }: { summary: ReactNode; children: ReactNode; open?: boolean }) => (
  <details className="text-sm" open={open}>
    <summary className="cursor-pointer text-teal-800">{summary}</summary>
    <div className="mt-2">{children}</div>
  </details>
);

export const PartyLink = ({ party, individual = false }: { party: Party; individual?: boolean }) => {
  if (individual) {
    return <span className="text-stone-500">An individual (name withheld)</span>;
  }
  const name = displayName(party.name);
  const title = party.name ?? undefined;
  if (party.ein !== null && party.listed) {
    return (
      <>
        <Link to={`/org/${party.ein}`} title={title}>
          {name ?? formatEin(party.ein)}
        </Link>{' '}
        <span className="text-stone-500 text-xs">{formatEin(party.ein)}</span>
      </>
    );
  }
  if (party.ein !== null) {
    return (
      <>
        <span title={title}>{name ?? 'Name not stated'}</span> <span className="text-stone-500 text-xs">{formatEin(party.ein)} · no page: no return on record</span>
      </>
    );
  }
  if (party.id !== null) {
    return <span title={title}>{name ?? identifierTail(party.id)}</span>;
  }
  return (
    <>
      <span title={title}>{name ?? 'Name not stated'}</span>
      {party.project === null ? <span className="text-stone-500 text-xs"> · as the funder wrote it</span> : <span className="text-stone-500 text-xs"> · a project of the sponsor</span>}
    </>
  );
};

/** The source, the return or page or award, and the period; the return links to its public copy. */
export const ProvenanceNote = ({ provenance, filerEin }: { provenance: Provenance; filerEin: string | null }) => (
  <span className="text-xs text-stone-500">
    {sourceLabel(provenance.source)}
    {provenance.objectId !== null && provenance.source === 'irs-990' ? (
      <>
        {' · '}
        {filerEin === null ? (
          <>return {provenance.objectId}</>
        ) : (
          <a href={returnUrl(filerEin, provenance.objectId)} className="underline decoration-stone-300 hover:decoration-stone-700" rel="noreferrer" title="This return on ProPublica Nonprofit Explorer">
            return {provenance.objectId}
          </a>
        )}
      </>
    ) : null}
    {provenance.page !== null ? (
      <>
        {' · '}
        <a href={provenance.page} className="underline" rel="noreferrer">
          page
        </a>
        {provenance.capturedAt !== null ? ` captured ${provenance.capturedAt.slice(0, 10)}` : ''}
      </>
    ) : null}
    {provenance.source === 'usaspending' ? ` · award record` : ''}
    {` · ${formatPeriod(provenance.period)}`}
  </span>
);

const TIER_LABEL: Readonly<Record<string, string>> = { exact: 'exact name match', rule: 'name and place rule', vector: 'nearest name, corroborated' };

export const GrantRow = ({ edge, side }: { edge: GrantEdge; side: 'funder' | 'recipient' }) => {
  const party = side === 'funder' ? edge.funder : edge.recipient;
  return (
    <li className="py-2 border-b border-stone-100 last:border-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <div className="text-stone-900">
          <PartyLink party={party} individual={side === 'recipient' && edge.individual} />
        </div>
        <div className="font-medium tabular-nums">{formatAmount(edge.amount)}</div>
      </div>
      {edge.purpose !== null ? <div className="text-sm text-stone-700">{edge.purpose}</div> : null}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <ProvenanceNote provenance={edge.provenance} filerEin={edge.funder.ein} />
        {edge.program !== null ? <Badge>{edge.program}</Badge> : null}
        {side === 'recipient' && edge.basis === 'resolved' ? <Badge tone="teal">resolved: {TIER_LABEL[edge.tier ?? ''] ?? edge.tier}</Badge> : null}
        {edge.confirmation !== null ? (
          <Badge tone={edge.confirmation.amountAgrees ? 'teal' : 'amber'}>{edge.confirmation.amountAgrees ? 'confirmed by a later filing' : 'confirmed by a later filing; amounts differ'}</Badge>
        ) : null}
        {edge.part === 'web-grant' && edge.confirmation === null ? <Badge tone="amber">from the funder's website; not yet in a filing</Badge> : null}
      </div>
    </li>
  );
};

export const Pager = ({ page, pages, onPage }: { page: number; pages: number; onPage: (page: number) => void }) =>
  pages <= 1 ? null : (
    <div className="mt-3 flex items-center gap-3 text-sm">
      <button type="button" className="rounded border border-stone-300 px-2 py-1 disabled:opacity-40" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </button>
      <span>
        Page {formatCount(page)} of {formatCount(pages)}
      </span>
      <button type="button" className="rounded border border-stone-300 px-2 py-1 disabled:opacity-40" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next
      </button>
    </div>
  );
